import { useState, useEffect, useCallback } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, CalendarCheck2, PlugZap, Unplug, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

const MESSAGES = {
  1: "Google Calendar connecté ✅",
  invalid_state: "Session OAuth expirée, merci de réessayer.",
  missing_code_or_state: "Réponse Google incomplète, merci de réessayer.",
  token_exchange_failed: "Échange de jeton Google échoué.",
  calendar_provisioning_failed: "Impossible de créer les calendriers Bati Assistant dans votre compte Google.",
  user_not_found: "Utilisateur introuvable, reconnectez-vous.",
  no_access_token: "Google n'a pas renvoyé de jeton d'accès.",
};

export const GoogleCalendarSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingPref, setSavingPref] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.getGoogleCalendarStatus();
      setStatus(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Handle OAuth callback params
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected") === "1") {
      toast.success(MESSAGES["1"]);
      params.delete("google_connected");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("google_error")) {
      const code = params.get("google_error");
      toast.error(MESSAGES[code] || `Erreur OAuth Google : ${code}`);
      params.delete("google_error");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [refresh]);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const { data } = await api.getGoogleCalendarAuthUrl();
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(getErrorMessage(e, "Impossible de démarrer la connexion Google"));
      setConnecting(false);
    }
  };

  const handleDisconnect = async (removeCalendars) => {
    try {
      setDisconnecting(true);
      await api.disconnectGoogleCalendar(removeCalendars);
      toast.success("Google Calendar déconnecté");
      await refresh();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de déconnexion"));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const { data } = await api.syncAllGoogleCalendar();
      const { orders, absences, savs } = data.synced || {};
      toast.success(`Synchronisation lancée — ${orders || 0} chantier(s), ${absences || 0} absence(s), ${savs || 0} SAV`);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de synchronisation"));
    } finally {
      setSyncing(false);
    }
  };

  const togglePref = async (key) => {
    if (!status) return;
    const next = { sync_orders: status.sync_orders, sync_absences: status.sync_absences, sync_savs: status.sync_savs, [key]: !status[key] };
    try {
      setSavingPref(true);
      await api.updateGoogleCalendarPreferences(next);
      setStatus({ ...status, ...next });
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de sauvegarde"));
    } finally {
      setSavingPref(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const configured = status?.configured;
  const connected = status?.connected;

  return (
    <Card data-testid="google-calendar-section">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <CalendarCheck2 className="h-5 w-5 text-primary" />
          Google Calendar
          {connected ? (
            <Badge className="ml-2 bg-green-100 text-green-800 border-green-200" data-testid="gcal-connected-badge">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Connecté
            </Badge>
          ) : (
            <Badge variant="outline" className="ml-2" data-testid="gcal-disconnected-badge">
              <XCircle className="h-3 w-3 mr-1" />
              Non connecté
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {!configured && (
          <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="gcal-not-configured">
            L'intégration n'est pas encore configurée côté serveur. Les identifiants OAuth Google doivent être renseignés
            dans les variables d'environnement <code className="font-mono text-xs">GOOGLE_CLIENT_ID</code> et <code className="font-mono text-xs">GOOGLE_CLIENT_SECRET</code>.
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Connectez votre compte Google pour synchroniser automatiquement vos chantiers, absences et SAV
          dans 3 calendriers dédiés (créés automatiquement dans votre agenda Google).
        </p>

        {connected && (
          <div className="space-y-3 text-sm">
            <div>
              <span className="text-muted-foreground">Compte Google :</span>{" "}
              <span className="font-medium" data-testid="gcal-email">{status.connected_email}</span>
            </div>

            <div className="space-y-2 border-t pt-3">
              <p className="font-medium text-sm">Que synchroniser ?</p>
              <div className="flex items-center justify-between p-3 rounded-sm border">
                <div>
                  <p className="font-medium text-sm">Chantiers (commandes)</p>
                  <p className="text-xs text-muted-foreground">Créés dans "Bati Assistant - Chantiers"</p>
                </div>
                <Switch
                  checked={!!status.sync_orders}
                  disabled={savingPref}
                  onCheckedChange={() => togglePref("sync_orders")}
                  data-testid="toggle-gcal-orders"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-sm border">
                <div>
                  <p className="font-medium text-sm">Absences salariés</p>
                  <p className="text-xs text-muted-foreground">Créés dans "Bati Assistant - Absences"</p>
                </div>
                <Switch
                  checked={!!status.sync_absences}
                  disabled={savingPref}
                  onCheckedChange={() => togglePref("sync_absences")}
                  data-testid="toggle-gcal-absences"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-sm border">
                <div>
                  <p className="font-medium text-sm">SAV (interventions)</p>
                  <p className="text-xs text-muted-foreground">Créés dans "Bati Assistant - SAV"</p>
                </div>
                <Switch
                  checked={!!status.sync_savs}
                  disabled={savingPref}
                  onCheckedChange={() => togglePref("sync_savs")}
                  data-testid="toggle-gcal-savs"
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          {!connected ? (
            <Button onClick={handleConnect} disabled={connecting || !configured} data-testid="gcal-connect-btn">
              {connecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />}
              Connecter Google Calendar
            </Button>
          ) : (
            <>
              <Button onClick={handleSync} disabled={syncing} variant="default" data-testid="gcal-sync-btn">
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Tout resynchroniser
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={disconnecting} data-testid="gcal-disconnect-btn">
                    {disconnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unplug className="h-4 w-4 mr-2" />}
                    Déconnecter
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Déconnecter Google Calendar ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Vos jetons d'accès seront supprimés. Les événements déjà créés dans votre agenda Google
                      resteront en place. Les futures modifications ne seront plus synchronisées.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDisconnect(false)} data-testid="gcal-confirm-disconnect">
                      Déconnecter (garder les événements)
                    </AlertDialogAction>
                    <AlertDialogAction onClick={() => handleDisconnect(true)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="gcal-confirm-disconnect-remove">
                      Déconnecter & supprimer les 3 calendriers
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GoogleCalendarSection;
